<template>
    <div v-if="elements" :class="{ 'mb-5 mt-2': noElements }">
        <!-- Create Element Controls - Insert Above -->
        <div v-show="noElements">
            <p class="text-secondary mt-4 pt-1 mb-1" :class="{ 'text-center': centered }">No content added yet.</p>
        </div>
        <div
            v-show="!maxItemsReached"
            :class="{
                'd-flex justify-content-center pb-2': noElements && centered,
                'insert-btns': elements.length > 0,
            }"
        >
            <rich-content-options
                @create="createElement($event, 0)"
                :allowed-element-types="allowedElementTypes"
                class="my-3 py-2"
            ></rich-content-options>
        </div>
        <!-- Existing Elements -->
        <div v-for="(element, index) in elements" :key="index">
            <div class="d-flex">
                <!-- Left Side Tools -->
                <div class="flex-grow-0 flex-shrink-0 d-flex flex-column text-block-tools mt-1">
                    <div class="mb-2">
                        <button type="button" class="btn btn-transparent py-0 px-1" @click="removeElement(index)">
                            <span class="icon icon-glyphicons-basic-bin"></span>
                        </button>
                    </div>
                    <div class="mb-3" v-show="maxItems > 1 || maxItems === null">
                        <div class="btn-group-vertical">
                            <button
                                type="button"
                                class="btn btn-transparent btn-sm py-0 px-1"
                                @click="shiftElementIndex(index, -1)"
                            >
                                <span class="icon icon-glyphicons-basic-arrow-up"></span>
                            </button>
                            <button
                                type="button"
                                class="btn btn-transparent btn-sm py-0 px-1"
                                @click="shiftElementIndex(index, 1)"
                            >
                                <span class="icon icon-glyphicons-basic-arrow-down"></span>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="flex-grow-1">
                    <div v-if="element.element_type === 'text'">
                        <rich-text v-model="element.content"></rich-text>
                        <base-form
                            v-if="element.typing_animation_enabled"
                            class="mt-3"
                            v-model="elements[index]"
                            :fields="textFormFields"
                            :condensed-style="true"
                            :inline-style="true"
                        ></base-form>
                    </div>
                    <div v-else-if="element.element_type === 'youtube'">
                        <div class="row mb-3">
                            <div class="col-12">
                                <input
                                    v-model="element.content"
                                    class="form-control"
                                    placeholder="Enter the Video ID or URL of a YouTube video."
                                />
                                <small class="text-secondary">
                                    A YouTube video's ID can be found from it's URL, e.g.
                                    https://www.youtube.com/watch?v=<span class="font-weight-bolder">1234</span>
                                </small>
                            </div>
                        </div>
                        <base-form
                            v-model="elements[index]"
                            :fields="youtubeVideoFormFields"
                            :condensed-style="true"
                            :inline-style="true"
                        ></base-form>
                    </div>
                    <div v-else-if="element.element_type === 'vimeo'">
                        <base-form
                            v-model="elements[index]"
                            :fields="vimeoVideoFormFields"
                            :condensed-style="true"
                            :inline-style="true"
                        ></base-form>
                    </div>
                    <div v-else-if="element.element_type === 'vr'">
                        <file-upload
                            v-model="element.stored_file"
                            placeholder="Drop a video file here"
                            :replaceable="fileUploadsReplaceable"
                            :max-bytes="300000000"
                            :direct-upload="true"
                            accept=".mp4"
                            placeholder-icon-class="icon-glyphicons-basic-film"
                            max-bytes-message="Please reduce the quality of your file, or embed using YouTube or Vimeo."
                        ></file-upload>
                        <base-form
                            v-model="elements[index]"
                            :fields="videoFormFields"
                            :condensed-style="true"
                            :inline-style="true"
                        ></base-form>
                    </div>
                    <textarea
                        v-else-if="element.element_type === 'iframe'"
                        v-model="element.content"
                        maxlength="5000"
                        id="iframe-content"
                        placeholder="Paste the iframe code here (i.e. <iframe ...>)."
                        class="form-control"
                        rows="6"
                    ></textarea>
                    <div v-else-if="element.element_type === 'video'">
                        <!-- Video Upload -->
                        <file-upload
                            v-model="element.stored_file"
                            placeholder="Drop a video file here"
                            :replaceable="fileUploadsReplaceable"
                            :max-bytes="1000000000"
                            :direct-upload="true"
                            accept=".mp4"
                            placeholder-icon-class="icon-glyphicons-basic-film"
                            max-bytes-message="Please reduce the size of your video file."
                        ></file-upload>
                        <div class="d-flex align-items-center mb-2 pb-1" style="max-width: 320px">
                            <div v-if="element.stored_file" class="flex-grow-1 d-flex align-items-center">
                                <div
                                    v-if="element.stored_file.file_processing_status === 'in_progress'"
                                    class="small text-light-wave ml-1"
                                >
                                    <info-popover
                                        content="Optimization can take a few minutes. You can close this edit window and check back later."
                                        placement="top"
                                    >
                                        Optimizing video...
                                    </info-popover>
                                </div>
                                <div
                                    v-else-if="element.stored_file.file_processing_status === 'successful'"
                                    class="small ml-1"
                                >
                                    <span class="icon icon-glyphicons-basic-circle-check text-success icon-mr"></span>
                                    <span>Video Optimized</span>
                                </div>
                                <div
                                    v-else-if="element.stored_file.file_processing_status === 'failed'"
                                    class="small ml-1 mr-2"
                                >
                                    <span class="icon icon-glyphicons-basic-triangle-alert text-warning icon-mr"></span>
                                    <span>Optimization Failed</span>
                                </div>
                                <button
                                    v-if="
                                        element.stored_file &&
                                        element.stored_file.id &&
                                        ['not_started', 'failed'].includes(element.stored_file.file_processing_status)
                                    "
                                    class="btn btn-sm btn-link p-0 small mr-1"
                                    @click="initiateElementFileOptimization(element)"
                                >
                                    <span v-if="element.stored_file.file_processing_status === 'not_started'">
                                        <span class="icon icon-glyphicons-basic-magic-wand mr-xs"></span>
                                        <span>Optimize Video</span>
                                    </span>
                                    <span v-else>
                                        <span class="icon icon-100 icon-glyphicons-basic-refresh mr-xs"></span>
                                        <span>Retry</span>
                                    </span>
                                </button>
                            </div>
                            <div class="ml-1" v-if="element.stored_file && !element.stored_file.isNewlyUploaded">
                                <div class="dropdown">
                                    <button
                                        class="btn btn-sm btn-link p-0 px-1"
                                        aria-expanded="false"
                                        data-toggle="dropdown"
                                    >
                                        <span class="icon icon-glyphicons-basic-subtitles icon-mr"></span>
                                        <span>Subtitles</span>
                                    </button>
                                    <div class="dropdown-menu medium shadow-sm">
                                        <div class="dropdown-header">
                                            <!-- Manually Uploaded -->
                                            <div v-if="element.stored_file.captions_manually_uploaded">
                                                <span
                                                    class="icon icon-glyphicons-basic-circle-check text-success icon-mr"
                                                ></span>
                                                <span class="text-secondary">Manually Added</span>
                                            </div>
                                            <!-- Auto-Generated -->
                                            <div v-else-if="element.stored_file.captions_generation_status === 'successful'">
                                                <span
                                                    class="icon icon-glyphicons-basic-circle-check text-success icon-mr"
                                                ></span>
                                                <span class="text-secondary">Auto-Generated (AI)</span>
                                            </div>
                                            <div
                                                v-else-if="element.stored_file.captions_generation_status === 'in_progress'"
                                                class="text-light-wave ml-1"
                                            >
                                                <info-popover
                                                    content="Generating subtitles can take a few minutes. You can close this edit window and check back later."
                                                    placement="top"
                                                >
                                                    Generating subtitles...
                                                </info-popover>
                                            </div>
                                            <!-- Auto-Generate Button -->
                                            <div
                                                v-else-if="
                                                    ['not_started', 'failed'].includes(
                                                        element.stored_file.captions_generation_status
                                                    )
                                                "
                                            >
                                                <info-popover
                                                    :content="freeTierActive ? 'Plan upgrade required' : ''"
                                                    placement="top"
                                                >
                                                    <button
                                                        class="btn btn-sm btn-primary"
                                                        @click.stop="generateCaptions(element)"
                                                        :disabled="freeTierActive"
                                                    >
                                                        <img
                                                            src="/static/general/img/icons/shines-white.svg"
                                                            height="12"
                                                            class="mr-1 align-middle"
                                                        />
                                                        <span class="align-middle">Auto-Generate (AI)</span>
                                                    </button>
                                                </info-popover>
                                            </div>
                                        </div>
                                        <div>
                                            <div class="dropdown-divider"></div>
                                            <button class="dropdown-item" @click="showEditCaptions(element, index)">
                                                <span
                                                    class="icon icon-100 icon-glyphicons-basic-pencil mr-1 align-middle"
                                                ></span>
                                                <span class="align-middle">Show & Edit</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="mb-3 mt-3 mr-5" v-if="element.showCaptionsEdit">
                            <textarea
                                v-model="element.stored_file.captions_vtt_text"
                                rows="8"
                                class="form-control form-control-sm"
                                placeholder="WEBVTT"
                                ref="elementCaptionsInput"
                            ></textarea>
                            <div class="d-flex justify-content-end mt-2">
                                <button class="btn btn-sm btn-light" @click="hideEditCaptions(element)">Done</button>
                            </div>
                        </div>
                        <base-form
                            v-model="elements[index]"
                            :fields="videoFormFields"
                            :condensed-style="true"
                            :inline-style="true"
                        ></base-form>
                    </div>
                    <div v-else-if="element.element_type === 'audio'">
                        <file-upload
                            v-model="element.stored_file"
                            placeholder="Drop an audio file here"
                            :replaceable="fileUploadsReplaceable"
                            :max-bytes="32000000"
                            :direct-upload="true"
                            accept=".mp3"
                            placeholder-icon-class="icon-glyphicons-basic-equalizer-bars"
                        ></file-upload>
                        <a
                            @click="showTranscriptInput(elements[index])"
                            class="btn-sm badge badge-light badge-pill mr-2 mb-3"
                            v-show="!elements[index].showTranscriptInput"
                        >
                            <span class="icon icon-glyphicons-basic-plus mr-1"></span> Text Transcript
                        </a>
                        <base-form
                            v-model="elements[index]"
                            :fields="audioFileFields"
                            :condensed-style="true"
                            class="limit-width"
                        ></base-form>
                    </div>
                    <div v-else-if="element.element_type === 'downloadable'">
                        <file-upload
                            v-model="element.stored_file"
                            placeholder="Drop a downloadable file here"
                            :replaceable="fileUploadsReplaceable"
                            :max-bytes="32000000"
                            accept="any file type"
                            :direct-upload="true"
                        ></file-upload>
                        <div class="row pt-2">
                            <div class="col-12 col-md-8">
                                <base-form
                                    v-model="elements[index]"
                                    :fields="downloadableFileFields"
                                    :condensed-style="true"
                                    :inline-style="true"
                                ></base-form>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="element.element_type === 'pdf'">
                        <file-upload
                            v-model="element.stored_file"
                            placeholder="Drop a PDF file here"
                            :replaceable="fileUploadsReplaceable"
                            :max-bytes="100000000"
                            accept=".pdf"
                            :direct-upload="true"
                        ></file-upload>
                        <div class="row pt-2">
                            <div class="col-12 col-md-8">
                                <base-form
                                    v-model="elements[index]"
                                    :fields="pdfFields"
                                    :condensed-style="true"
                                    :inline-style="true"
                                ></base-form>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="element.element_type === 'image'">
                        <file-upload
                            v-model="element.stored_file"
                            placeholder="Drop an image here"
                            :replaceable="fileUploadsReplaceable"
                            :max-bytes="maxImageBytes"
                            accept=".png|.jpg|.gif|.svg"
                            :warning-bytes="warningImageBytes"
                            file-type-label="image"
                            :direct-upload="true"
                        ></file-upload>
                    </div>
                    <div v-else-if="element.element_type === 'image_ai'" class="d-flex flex-wrap pb-2 pt-1">
                        <div class="flex-grow-1 mr-3">
                            <div class="mb-2">
                                <textarea
                                    v-model="element.currentPrompt"
                                    type="text"
                                    class="form-control form-control-sm mw-100 mb-1"
                                    maxlength="1000"
                                    placeholder="Describe your desired image here..."
                                    rows="5"
                                    dir="auto"
                                ></textarea>
                            </div>
                            <div class="d-flex justify-content-end">
                                <div>
                                    <span v-show="freeTierActive" class="badge badge-light badge-tiny mr-2 align-middle"
                                        >Paid Only</span
                                    >
                                    <button
                                        class="btn btn-primary btn-sm"
                                        :disabled="elementAIBtnsDisabled(element)"
                                        @click="generateMedia(element)"
                                    >
                                        <span class="icon icon-glyphicons-basic-magic-wand mr-1 align-middle"></span>
                                        <span v-if="element.isLoading">Generating...</span>
                                        <span v-else-if="element.stored_file">Regenerate</span>
                                        <span v-else>Generate</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="flex-grow-0 mx-4 mt-4 mt-sm-0 d-flex justify-content-center">
                            <div class="position-relative">
                                <img
                                    v-if="element.stored_file"
                                    :src="element.stored_file.signed_url"
                                    class="rounded border generated-image"
                                    :class="{
                                        'generated-image-loading': element.isLoading,
                                        'generated-image-sm': narrowMode,
                                        'generated-image-lg': !narrowMode,
                                    }"
                                />
                                <div
                                    v-else
                                    class="rounded border bg-light d-flex justify-content-center align-items-center generated-image px-3"
                                    :class="{
                                        'generated-image-loading': element.isLoading,
                                        'generated-image-sm small': narrowMode,
                                        'generated-image-lg': !narrowMode,
                                    }"
                                >
                                    <div v-if="element.errorMessage" class="text-danger text-center small">
                                        <span class="icon icon-glyphicons-basic-circle-alert mr-1"></span>
                                        <span>{{ element.errorMessage }}</span>
                                    </div>
                                    <span
                                        v-else-if="!element.isLoading"
                                        class="font-weight-bold generated-image-placeholder text-center"
                                        >Your image preview will appear here</span
                                    >
                                </div>
                                <div
                                    v-show="element.isLoading"
                                    :class="{ 'd-flex': element.isLoading }"
                                    class="align-items-center justify-content-center generated-image-overlay"
                                >
                                    <div class="d-flex flex-column justify-content-center align-items-center">
                                        <div class="mb-2">
                                            <span
                                                class="spinner-border text-primary"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                        </div>
                                        <div class="small font-weight-500 text-secondary">
                                            <div class="d-inline-block" style="min-width: 22px">
                                                {{ element.loadingTimeElapsed }}
                                            </div>
                                            <div class="d-inline-block">s</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="element.element_type === 'audio_ai'" class="pb-2 pt-1">
                        <div class="limit-width">
                            <textarea
                                v-model="element.currentPrompt"
                                type="text"
                                class="form-control form-control-sm mw-100 mb-2"
                                maxlength="4096"
                                placeholder="Enter the text you want to convert to speech here..."
                                rows="5"
                                dir="auto"
                            ></textarea>
                            <textarea
                                v-model="element.currentInstructions"
                                type="text"
                                class="form-control form-control-sm mw-100 mb-2"
                                maxlength="4096"
                                placeholder="Describe the mood or tone of the speech here..."
                                rows="3"
                                dir="auto"
                            ></textarea>
                            <div v-if="element.errorMessage" class="text-danger small">
                                <span class="icon icon-glyphicons-basic-circle-alert mr-1"></span>
                                <span>{{ element.errorMessage }}</span>
                            </div>
                            <div class="d-flex mt-3">
                                <div class="flex-grow-1">
                                    <div class="d-flex align-items-center mr-2">
                                        <div class="flex-grow-0 mr-1 small">Voice:</div>
                                        <div class="flex-grow-0">
                                            <select
                                                v-model="element.ai_gen_variation"
                                                @change="handleElementVoiceChange(element)"
                                                :disabled="element.isLoading"
                                                class="form-control form-control-sm"
                                            >
                                                <option value="alloy">Alloy (neutral)</option>
                                                <option value="shimmer">Shimmer (neutral)</option>
                                                <option value="nova">Nova (high)</option>
                                                <option value="echo">Echo (low)</option>
                                                <option value="onyx">Onyx (lowest)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-grow-0 flex-shrink-0 d-flex align-items-center">
                                    <span v-show="freeTierActive" class="badge badge-light badge-tiny mr-3 align-middle"
                                        >Paid Only</span
                                    >
                                    <div v-show="element.isLoading">
                                        <div class="d-flex align-items-center small text-secondary">
                                            <div style="min-width: 26px" class="mr-2">
                                                <div class="d-inline-block" style="min-width: 20px">
                                                    {{ element.loadingTimeElapsed }}
                                                </div>
                                                <div class="d-inline-block">s</div>
                                            </div>
                                            <div
                                                class="spinner-border spinner-border-sm text-primary mr-2"
                                                role="status"
                                            >
                                                <span class="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        class="btn btn-primary btn-sm nowrap"
                                        :disabled="elementAIBtnsDisabled(element)"
                                        @click="generateMedia(element)"
                                    >
                                        <span class="icon icon-glyphicons-basic-magic-wand mr-1 align-middle"></span>
                                        <span v-if="element.isLoading">Generating Speech...</span>
                                        <span v-else-if="element.stored_file">Regenerate Speech</span>
                                        <span v-else>Generate Speech</span>
                                    </button>
                                </div>
                            </div>
                            <div v-if="element.stored_file" class="mt-3">
                                <div class="mb-2">
                                    <audio controls :src="element.stored_file.signed_url">
                                        Your browser does not support the
                                        <code>audio</code> element.
                                    </audio>
                                </div>
                                <div class="ml-2">
                                    <base-form
                                        v-model="elements[index]"
                                        :fields="audioFileFields"
                                        :condensed-style="true"
                                    ></base-form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <file-upload
                            v-model="element.stored_file"
                            placeholder="Drop an image here"
                            :replaceable="fileUploadsReplaceable"
                            :max-bytes="32000000"
                            accept=".png|.jpg|.gif|.svg"
                            :direct-upload="true"
                        ></file-upload>
                    </div>
                    <div v-show="element.element_type == 'image'" class="mb-2">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-0 d-flex align-items-center">
                                <textarea
                                    v-if="allowedImageFields.includes('accessibility_alt_text')"
                                    v-model="element.accessibility_alt_text"
                                    type="text"
                                    class="form-control form-control-sm mw-100"
                                    style="width: 320px"
                                    maxlength="1200"
                                    placeholder="Screen reader description"
                                    rows="1"
                                    dir="auto"
                                ></textarea>
                                <div
                                    v-if="allowedImageFields.includes('display_width')"
                                    class="d-flex align-items-center ml-3"
                                >
                                    <span class="text-secondary mr-2 font-weight-500 small">Width:</span>
                                    <input
                                        v-model="element.display_width"
                                        type="text"
                                        class="form-control form-control-sm"
                                        style="width: 50px; margin-right: 3px"
                                    />
                                    <select
                                        v-model="element.display_width_units"
                                        class="form-control form-control-sm"
                                        style="width: 60px"
                                    >
                                        <option value="px">px</option>
                                        <option value="%">%</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Create Element Controls - Insert Below -->
            <div v-show="!maxItemsReached">
                <div class="insert-btns">
                    <rich-content-options
                        @create="createElement($event, index + 1)"
                        :allowed-element-types="allowedElementTypes"
                        class="my-3 py-2"
                    ></rich-content-options>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { differenceInMilliseconds } from "date-fns"
import http from "@/axios.js"
import RichText from "@/components/RichText"
import BaseForm from "@/components/BaseForm"
import FileUpload from "@/components/FileUpload"
import RichContentOptions from "@/components/RichContentOptions"
import InfoPopover from "@/components/InfoPopover"

export default {
    name: "RichContentEditor",
    components: {
        BaseForm,
        RichText,
        FileUpload,
        RichContentOptions,
        InfoPopover,
    },
    props: {
        // This component accepts a list of elements to be edited.
        // The way this component works is hacky - this component doesn't always emit
        // an "input" event up to the parent, the elements contained in the provided lists are
        // updated directly, and those changes are reflected in the parent.
        value: {
            type: Array,
            default: function () {
                return []
            },
        },
        maxItems: {
            type: Number,
            required: false,
            default: null,
        },
        showInfoFooters: {
            type: Boolean,
            required: false,
            default: true,
        },
        fileUploadsReplaceable: {
            type: Boolean,
            required: false,
            default: true,
        },
        allowedElementTypes: {
            type: Array,
            required: false,
            default: function () {
                return ["text", "image", "audio", "video", "vimeo", "youtube", "iframe", "pdf", "downloadable"]
            },
        },
        allowedImageFields: {
            type: Array,
            required: false,
            default() {
                return ["accessibility_alt_text", "display_width"]
            },
        },
        allowedVideoFields: {
            type: Array,
            required: false,
            default() {
                return [
                    "autoplay_video",
                    "loop_media",
                    "play_video_before_continue",
                    "continue_after_video_finished",
                    "hide_video_controls",
                    "display_full_screen",
                ]
            },
        },
        allowedAudioFields: {
            type: Array,
            required: false,
            default() {
                return [
                    "accessibility_alt_text",
                    "autoplay_video",
                    "loop_media",
                    "play_video_before_continue",
                    "continue_after_video_finished",
                ]
            },
        },
        // A list of properties that should be set to true by default when new elements are created.
        defaultCheckedOptions: {
            type: Array,
            required: false,
            default() {
                return ["autoplay_video"]
            },
        },
        centered: {
            type: Boolean,
            required: false,
            default: true,
        },
        maxImageBytes: {
            type: Number,
            required: false,
            default() {
                return 5000000
            },
        },
        warningImageBytes: {
            type: Number,
            required: false,
            default() {
                return 2000000
            },
        },
        narrowMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            elements: [],
            statusTimeoutIds: [],
            mediaGenIsLoading: false,
            downloadableFileFields,
            pdfFields,
            textFormFields,
        }
    },
    computed: {
        freeTierActive() {
            return this.$store.getters.freeTierActive
        },
        currentOrganizationId() {
            return this.$store.getters.currentOrganizationId
        },
        caseBaseUrl() {
            return this.$store.getters.caseBaseUrl
        },
        noElements() {
            return this.elements.length === 0
        },
        maxItemsReached() {
            if (!this.maxItems) {
                return false
            }
            return this.elements.length >= this.maxItems
        },
        vimeoVideoFormFields() {
            let vimeoFields = [
                {
                    name: "Vimeo Video URL",
                    key: "content",
                    type: "text",
                },
            ]
            return vimeoFields.concat(this.videoFormFields)
        },
        youtubeVideoFormFields() {
            let fields = Object.assign([], this.videoFormFields)
            // Remove the hide controls field, if included.
            let index = fields.findIndex((f) => f.key == "hide_video_controls")
            if (index != -1) {
                fields.splice(index, 1)
            }
            // Add the video height field, if excluded.
            index = fields.findIndex((f) => f.key == "height")
            if (index === -1) {
                fields.push({
                    name: "Video Height (pixels)",
                    key: "height",
                    placeholder: "Video height (e.g. 300)",
                    type: "number",
                })
            }
            return fields
        },
        videoFormFields() {
            const videoFields = {
                autoplay_video: {
                    name: "Autoplay",
                    type: "checkbox",
                },
                loop_media: {
                    name: "Loop",
                    type: "checkbox",
                },
                play_video_before_continue: {
                    name: "Disable continue button until video ends",
                    type: "checkbox",
                },
                continue_after_video_finished: {
                    name: "Automatically continue to next screen after video ends",
                    type: "checkbox",
                },
                display_full_screen: {
                    name: "Display full-screen",
                    type: "checkbox",
                    subtext:
                        "If paired with other content-types, full-screen will be automatically closed when video finishes.",
                },
                hide_video_controls: {
                    name: "Disable skipping forward or backward",
                    type: "checkbox",
                },
                height: {
                    name: "Video height",
                    type: "number",
                },
            }
            let fields = []
            this.allowedVideoFields.forEach((field) => {
                fields.push({
                    key: field,
                    ...videoFields[field],
                })
            })
            return fields
        },
        audioFileFields() {
            const audioFields = {
                accessibility_alt_text: {
                    placeholder: "Paste a text transcript for your audio file here...",
                    type: "textarea",
                    rows: 3,
                    hidden(value) {
                        return !value.showTranscriptInput
                    },
                },
                autoplay_video: {
                    name: "Autoplay",
                    type: "checkbox",
                },
                loop_media: {
                    name: "Loop",
                    key: "loop_media",
                    type: "checkbox",
                },
                play_video_before_continue: {
                    name: "Disable Continue Button Until Complete",
                    type: "checkbox",
                },
                continue_after_video_finished: {
                    name: "Automatically continue to next screen after audio ends",
                    type: "checkbox",
                },
            }
            let fields = []
            this.allowedAudioFields.forEach((field) => {
                fields.push({
                    key: field,
                    ...audioFields[field],
                })
            })
            return fields
        },
    },
    watch: {
        value(newValue) {
            this.elements = newValue
            let vm = this
            this.elements.forEach((el, index) => {
                // Set a flag to show audio transcript inputs where text transcript content
                // has been set.
                if (el.element_type == "audio" && el.accessibility_alt_text) {
                    vm.$set(vm.elements[index], "showTranscriptInput", true)
                } else if (["image_ai", "audio_ai"].includes(el.element_type)) {
                    vm.$set(vm.elements[index], "currentPrompt", el.accessibility_alt_text)
                    vm.$set(vm.elements[index], "currentInstructions", el.ai_gen_instructions)
                    vm.$set(vm.elements[index], "savedPrompt", el.accessibility_alt_text)
                    vm.$set(vm.elements[index], "savedVariation", el.ai_gen_variation)
                }
            })
            this.clearStatusTimeouts()
        },
    },
    methods: {
        // Create an element.
        createElement(type, index) {
            // Need to make sure the stored_file property is initialized to null. This fixes a
            // very specific bug where if an empty image re-ordered into the position of an
            // in-memory image, the in-memory image is lost (the stored_file property is removed).
            let element = {
                image: null,
                element_type: type,
                content: "",
                display_width: 100,
                display_width_units: "%",
                stored_file: null,
                hide_video_controls: false,
            }
            if (type === "audio_ai") {
                element.ai_gen_variation = "alloy"
                element.ai_gen_instructions = ""
            }
            this.defaultCheckedOptions.forEach((option) => {
                element[option] = true
            })
            this.elements.splice(index, 0, element)
            this.$emit("input", this.elements)
        },
        // Delete an element.
        removeElement(index) {
            this.elements.splice(index, 1)
            this.$emit("input", this.elements)
        },
        // Shift an element's position up or down.
        shiftElementIndex(index, increment) {
            // Don't do anything if choice is already at the start or end of the array.
            if ((index == 0 && increment == -1) || (index + 1 >= this.elements.length && increment == 1)) {
                return
            }
            // Remove the element from the choice array.
            let element = this.elements.splice(index, 1)[0]
            // Put it back at the new position.
            this.elements.splice(index + increment, 0, element)
            this.$emit("input", this.elements)
        },
        clear() {
            this.elements = []
            this.$emit("input", this.elements)
        },
        createCaptions(element) {
            this.$set(element, "showCaptionsUpload", true)
        },
        // Show the transcript textarea input for an audio element.
        showTranscriptInput(element) {
            this.$set(element, "showTranscriptInput", true)
        },
        // Returns true if AI generation is disabled for a given element. Only allow
        // one AI generation process to be run at a time.
        elementAIBtnsDisabled(element) {
            return this.freeTierActive || this.mediaGenIsLoading || !element.currentPrompt || element.isLoading
        },
        // Generate image or audio so the user can see a preview.
        generateMedia(element) {
            this.mediaGenIsLoading = true

            // Store the current prompt as the accessibility text for the element.
            element.accessibility_alt_text = element.currentPrompt
            element.ai_gen_instructions = element.currentInstructions

            // Set a timer to display how long the image has been generating for.
            this.$set(element, "loadingStartTime", new Date())
            this.$set(element, "loadingTimeElapsed", 0)
            let timer = window.setInterval(() => {
                let diffInSec = differenceInMilliseconds(new Date(), element.loadingStartTime) / 1000
                element.loadingTimeElapsed = diffInSec.toFixed(1)
            }, 100)
            this.$set(element, "loadingTimer", timer)

            // Set loading and error message properties on the element.
            this.$set(element, "isLoading", true)
            this.$set(element, "errorMessage", "")

            const mediaTypes = {
                image_ai: "image",
                audio_ai: "audio",
            }
            let payload = {
                prompt: element.currentPrompt,
                organizationId: this.currentOrganizationId,
                mediaType: mediaTypes[element.element_type],
                variation: element.ai_gen_variation,
                instructions: element.currentInstructions,
            }
            if (element.stored_file) {
                payload.storedFileId = element.stored_file.id
                payload.storedFileAccessKey = element.stored_file.access_key
            }
            let vm = this
            http.post("/api/generate-media/", payload)
                .then((response) => {
                    vm.$set(element, "stored_file", response.data)
                    vm.$set(element, "savedPrompt", element.currentPrompt)
                    vm.$set(element, "accessibility_alt_text", element.currentPrompt)
                    vm.$set(element, "ai_gen_instructions", element.currentInstructions)
                    vm.$set(element, "savedVariation", element.ai_gen_variation)
                })
                .catch((error) => {
                    element.isLoading = false
                    vm.mediaGenIsLoading = false
                    vm.$set(element, "errorMessage", error.response.data.message)
                })
                .finally(() => {
                    element.isLoading = false
                    window.clearInterval(element.loadingTimer)
                    element.loadingTimer = null
                    vm.mediaGenIsLoading = false
                })
        },
        // Handle the user changing the AI-audio voice selector.
        handleElementVoiceChange(element) {
            if (element.currentPrompt) {
                this.generateMedia(element)
            }
        },
        // Initiate the processing/optimization of a stored file associated with an element.
        initiateElementFileOptimization(element) {
            this.$set(element.stored_file, "file_processing_status", "in_progress")
            let vm = this
            http.put(`/api/elements/${element.id}/process-file/`)
                .then(() => {
                    vm.checkOptimizationStatus(element)
                })
                .catch(() => {
                    element.stored_file.file_processing_status = "not_started"
                })
        },
        // Check the status of file optimization associated with an element.
        checkOptimizationStatus(element) {
            let vm = this
            http.get(`/api/elements/${element.id}/process-file/`).then((response) => {
                element.stored_file.file_processing_status = response.data.status
                if (response.data.status == "in_progress") {
                    // File processing is in progress, so check status again in a few
                    // seconds.
                    let timeoutId = window.setTimeout(() => {
                        vm.checkOptimizationStatus(element)
                    }, 3000)
                    vm.statusTimeoutIds.push(timeoutId)
                }
            })
        },
        // Stop all timeouts associated with checking file optimization status.
        clearStatusTimeouts() {
            this.statusTimeoutIds.forEach((timeoutId) => {
                clearTimeout(timeoutId)
            })
        },
        // Show the captions textarea for an element.
        showEditCaptions(element, index) {
            this.$set(element, "showCaptionsEdit", true)
            let vm = this
            this.$nextTick(() => {
                let captionsInput = vm.$refs.elementCaptionsInput[index]
                captionsInput.focus()
                captionsInput.scrollTop = 0
            })
        },
        // Hide the captions textarea for an element.
        hideEditCaptions(element) {
            element.showCaptionsEdit = false
        },
        // Initiate captions generation for an element.
        generateCaptions(element) {
            this.$set(element.stored_file, "captions_generation_status", "in_progress")
            let vm = this
            http.put(`/api/stored_files/${element.stored_file.id}/generate-captions/`, {
                access_key: element.stored_file.access_key
            })
                .then(() => {
                    vm.checkCaptionsStatus(element)
                })
                .catch(() => {
                    element.stored_file.captions_generation_status = "not_started"
                })
        },
        // Check the status of file optimization associated with an element.
        checkCaptionsStatus(element) {
            let vm = this
            http.get(`/api/stored_files/${element.stored_file.id}/generate-captions/`, {
                params: {
                    access_key: element.stored_file.access_key
                }
            }).then((response) => {
                element.stored_file.captions_generation_status = response.data.status
                if (response.data.status === "in_progress") {
                    // File processing is in progress, so check status again in a few
                    // seconds.
                    let timeoutId = window.setTimeout(() => {
                        vm.checkCaptionsStatus(element)
                    }, 3000)
                    vm.statusTimeoutIds.push(timeoutId)
                } else if (response.data.status === "successful") {
                    element.stored_file.captions_vtt_text = response.data.captions_vtt_text
                }
            })
        },
    },
    created() {
        this.elements = this.value
    },
    destroyed() {
        this.clearStatusTimeouts()
    },
}

const downloadableFileFields = [
    {
        name: "Button Text",
        key: "content",
        type: "text",
        placeholder: "Download File",
        description: "A label for the download button.",
    },
]

const pdfFields = [
    {
        name: "Viewer Height",
        key: "height",
        type: "number",
        placeholder: "Viewer Height (e.g. 600)",
        subtext: "The height of the PDF viewer in pixels.",
    },
]

const textFormFields = [
    {
        name: "Typewriter Animation",
        key: "typing_animation_enabled",
        type: "switch",
        description: "When enabled, text will be animated to look like its being typed.",
    },
]
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.scss";

.insert-btns {
    margin-left: 50px;
}

.text-block-tools {
    width: 50px;
}

.generated-image-sm {
    height: 270px;
    width: 270px;
}

.generated-image-lg {
    height: 350px;
    width: 350px;
}

.generated-image {
    object-fit: cover;
}

.generated-image-loading {
    opacity: 0.3;
}

.generated-image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.generated-image-placeholder {
    color: $cover-img-placeholder;
}

.limit-width {
    max-width: 600px;
}
</style>